/**
 * Settings: the numbers and switches that change how the bot behaves, without a deploy.
 *
 * Each one is a decision NAB should be able to revisit after watching real traffic — how long a
 * session lasts, how sure the router has to be before it acts, how many times it will ask before
 * giving up. Compiling any of them in would mean a release to change a threshold, which is how a
 * threshold ends up never being changed.
 *
 * ## Why the tone box is here and not in the tools editor
 *
 * `tone` is the one instruction that reaches the model on a *knowledge* answer, where there is no
 * template to word. It belongs with the other settings that shape the bot's voice rather than with
 * any one tool, and it is per language because "warm and brief" reads differently in Derja.
 *
 * ## The two cards about not being able to help
 *
 * `starterChips` and `handoff` are both about the turn where the bot has no answer. They are here
 * rather than in the tools editor because neither belongs to a tool: the chips are the menu for a
 * customer who asked about something no tool covers, and the handoff is where that customer goes
 * instead. Both are copy the bank should own, so both are typed here and not compiled in.
 *
 * ## The LLM endpoint is shown and not editable
 *
 * The profile name comes from `configs/llm.yaml` and its key from the environment. Putting a
 * secret in a dashboard field is how a secret ends up in a screenshot in a ticket.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Card,
  EmptyState,
  Input,
  Textarea,
  saveSetting,
  type BotSettings,
} from "@nab/shared";

import { useBot } from "../config";
import { VersionBar } from "./VersionBar";

export function SettingsPage(): JSX.Element {
  const { t } = useTranslation("app");
  const { config, problem, save } = useBot();
  const settings = config?.settings ?? {};

  if (problem) return <EmptyState title={t("notReady")} description={problem} />;

  return (
    <div className="flex flex-col">
      <VersionBar />
      <div className="grid gap-4 p-4 lg:grid-cols-2">
        <Group
          title={t("settings.session")}
          settingKey="session"
          value={settings.session ?? {}}
          fields={[
            {
              path: "ttlMinutes",
              label: t("settings.ttl"),
              hint: t("settings.ttlHint"),
              kind: "number",
            },
            {
              path: "maxHistoryTurns",
              label: t("settings.history"),
              hint: t("settings.historyHint"),
              kind: "number",
            },
          ]}
          onSave={save}
        />

        <Group
          title={t("settings.router")}
          settingKey="router"
          value={settings.router ?? {}}
          fields={[
            {
              path: "minConfidence",
              label: t("settings.minConfidence"),
              hint: t("settings.minConfidenceHint"),
              kind: "number",
              step: 0.05,
            },
            {
              path: "maxClarifications",
              label: t("settings.maxClarifications"),
              hint: t("settings.maxClarificationsHint"),
              kind: "number",
            },
            {
              path: "coarseGate",
              label: t("settings.coarseGate"),
              hint: t("settings.coarseGateHint"),
              kind: "boolean",
            },
            {
              path: "coarseGateThreshold",
              label: t("settings.coarseGateThreshold"),
              hint: t("settings.coarseGateThresholdHint"),
              kind: "number",
              step: 0.05,
            },
          ]}
          onSave={save}
        />

        <Group
          title={t("settings.knowledge")}
          settingKey="knowledge"
          value={settings.knowledge ?? {}}
          fields={[
            { path: "topK", label: t("settings.topK"), kind: "number" },
            {
              path: "maxAnswerSentences",
              label: t("settings.maxSentences"),
              hint: t("settings.maxSentencesHint"),
              kind: "number",
            },
            {
              path: "verbatimFaq",
              label: t("settings.verbatimFaq"),
              hint: t("settings.verbatimFaqHint"),
              kind: "boolean",
            },
            {
              path: "verbatimMinScore",
              label: t("settings.verbatimMinScore"),
              hint: t("settings.verbatimMinScoreHint"),
              kind: "number",
            },
          ]}
          onSave={save}
        />

        <Group
          title={t("settings.offers")}
          settingKey="offers"
          value={settings.offers ?? {}}
          fields={[
            {
              path: "enabled",
              label: t("settings.offersEnabled"),
              hint: t("settings.offersEnabledHint"),
              kind: "boolean",
            },
            {
              path: "frequencyCapTurns",
              label: t("settings.frequencyCap"),
              hint: t("settings.frequencyCapHint"),
              kind: "number",
            },
          ]}
          onSave={save}
        />

        <ChipsCard chips={settings.starterChips ?? {}} />

        <HandoffCard handoff={settings.handoff ?? {}} />

        <ToneCard tone={settings.tone ?? {}} />

        <Card className="flex flex-col gap-2 p-4">
          <h3 className="text-heading-sm">{t("settings.languages")}</h3>
          <p className="text-body-sm text-textMuted">{t("settings.languagesHint")}</p>
          <pre className="overflow-x-auto rounded-sm bg-surfaceSunken p-3 font-mono text-label-md">
            {JSON.stringify(
              {
                languages: settings.languages,
                scripts: settings.scripts,
                templateFallback: settings.templateFallback,
              },
              null,
              2,
            )}
          </pre>
        </Card>
      </div>
    </div>
  );
}

interface Field {
  path: string;
  label: string;
  hint?: string;
  kind: "number" | "boolean";
  step?: number;
}

function Group({
  title,
  settingKey,
  value,
  fields,
  onSave,
}: {
  title: string;
  settingKey: string;
  value: Record<string, unknown>;
  fields: Field[];
  onSave: (what: () => Promise<unknown>, success?: string) => Promise<boolean>;
}): JSX.Element {
  const { t } = useTranslation("app");
  const [draft, setDraft] = useState<Record<string, unknown>>(value);
  const dirty = JSON.stringify(draft) !== JSON.stringify(value);

  return (
    <Card className="flex flex-col gap-3 p-4">
      <h3 className="text-heading-sm">{title}</h3>
      {fields.map((field) =>
        field.kind === "boolean" ? (
          <label key={field.path} className="flex flex-col gap-1 text-body-md">
            <span className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={Boolean(draft[field.path])}
                onChange={(event) =>
                  setDraft({ ...draft, [field.path]: event.target.checked })
                }
              />
              {field.label}
            </span>
            {field.hint ? (
              <span className="text-label-md text-textMuted">{field.hint}</span>
            ) : null}
          </label>
        ) : (
          <Input
            key={field.path}
            type="number"
            label={field.label}
            {...(field.hint ? { hint: field.hint } : {})}
            {...(field.step ? { step: field.step } : {})}
            value={String(draft[field.path] ?? "")}
            onChange={(event) =>
              setDraft({ ...draft, [field.path]: Number(event.target.value) })
            }
          />
        ),
      )}
      <div>
        <Button
          size="sm"
          variant="primary"
          disabled={!dirty}
          onClick={() =>
            onSave(
              () => saveSetting(settingKey, draft),
              t("settings.saved", { key: settingKey }),
            )
          }
        >
          {t("tools.save")}
        </Button>
      </div>
    </Card>
  );
}

function ChipsCard({ chips }: { chips: Record<string, string[]> }): JSX.Element {
  const { t } = useTranslation("app");
  const { save } = useBot();
  const [draft, setDraft] = useState(chips);
  const dirty = JSON.stringify(draft) !== JSON.stringify(chips);

  // One line per chip, which is how people write short lists. Splitting on save rather than on
  // every keystroke lets somebody press Enter and start typing without the row vanishing.
  const asText = (list: string[]): string => list.join("\n");
  const asList = (text: string): string[] =>
    text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

  const tags = Object.keys(draft).length > 0 ? Object.keys(draft) : ["en"];

  return (
    <Card className="flex flex-col gap-3 p-4">
      <h3 className="text-heading-sm">{t("settings.starterChips")}</h3>
      <p className="text-body-sm text-textMuted">{t("settings.starterChipsHint")}</p>
      {tags.map((tag) => (
        <Textarea
          key={tag}
          label={tag}
          rows={4}
          value={asText(draft[tag] ?? [])}
          onChange={(event) =>
            setDraft({ ...draft, [tag]: asList(event.target.value) })
          }
        />
      ))}
      <div>
        <Button
          size="sm"
          variant="primary"
          disabled={!dirty}
          onClick={() =>
            save(
              () => saveSetting("starterChips", draft),
              t("settings.saved", { key: "starterChips" }),
            )
          }
        >
          {t("tools.save")}
        </Button>
      </div>
    </Card>
  );
}

function HandoffCard({
  handoff,
}: {
  handoff: { type?: string; value?: string | null };
}): JSX.Element {
  const { t } = useTranslation("app");
  const { save } = useBot();
  const [draft, setDraft] = useState(handoff);
  const dirty = JSON.stringify(draft) !== JSON.stringify(handoff);

  // Choosing "nowhere" removes the key rather than setting it to undefined: the setting is read
  // back as JSON, where a present-but-undefined field is not a thing, and `exactOptionalPropertyTypes`
  // is right to refuse the shortcut.
  const chooseType = (next: string): void => {
    const updated: { type?: string; value?: string | null } = next ? { type: next } : {};
    if (draft.value !== undefined) updated.value = draft.value;
    setDraft(updated);
  };

  return (
    <Card className="flex flex-col gap-3 p-4">
      <h3 className="text-heading-sm">{t("settings.handoff")}</h3>
      <p className="text-body-sm text-textMuted">{t("settings.handoffHint")}</p>
      <label className="flex flex-col gap-1">
        <span className="text-label-md">{t("settings.handoffType")}</span>
        <select
          className="rounded-sm border border-border bg-surface p-2 text-body-sm"
          value={draft.type ?? ""}
          onChange={(event) => chooseType(event.target.value)}
        >
          <option value="">{t("settings.handoffNone")}</option>
          <option value="call_center">{t("settings.handoffCallCenter")}</option>
          <option value="branch">{t("settings.handoffBranch")}</option>
          <option value="agent">{t("settings.handoffAgent")}</option>
        </select>
      </label>
      <Input
        label={t("settings.handoffValue")}
        hint={t("settings.handoffValueHint")}
        value={draft.value ?? ""}
        onChange={(event) =>
          setDraft({ ...draft, value: event.target.value || null })
        }
      />
      <div>
        <Button
          size="sm"
          variant="primary"
          disabled={!dirty}
          onClick={() =>
            save(
              () => saveSetting("handoff", draft.type ? draft : {}),
              t("settings.saved", { key: "handoff" }),
            )
          }
        >
          {t("tools.save")}
        </Button>
      </div>
    </Card>
  );
}

function ToneCard({ tone }: { tone: Record<string, string> }): JSX.Element {
  const { t } = useTranslation("app");
  const { save } = useBot();
  const [draft, setDraft] = useState(tone);
  const dirty = JSON.stringify(draft) !== JSON.stringify(tone);

  return (
    <Card className="flex flex-col gap-3 p-4">
      <h3 className="text-heading-sm">{t("settings.tone")}</h3>
      <p className="text-body-sm text-textMuted">{t("settings.toneHint")}</p>
      {Object.keys(draft).length === 0 ? (
        <p className="text-body-sm text-textMuted">{t("settings.noTone")}</p>
      ) : null}
      {Object.entries(draft).map(([language, text]) => (
        <Textarea
          key={language}
          label={language}
          rows={2}
          value={text}
          onChange={(event) => setDraft({ ...draft, [language]: event.target.value })}
        />
      ))}
      <div>
        <Button
          size="sm"
          variant="primary"
          disabled={!dirty}
          onClick={() =>
            save(() => saveSetting("tone", draft), t("settings.saved", { key: "tone" }))
          }
        >
          {t("tools.save")}
        </Button>
      </div>
    </Card>
  );
}

export type { BotSettings };
